import sentry from '@/services/sentry'
import UnleashService from '@/services/unleash'
import { flags } from '@/utils/abtest.utils'
import { getCachedCurrentUser, saveCachedCurrentUser } from '@/vuex/modules/loading-page/loading-page.utils'
import {
  buildFlagCohortsContextValue,
  chunkFlags,
  getFlagsToRecord,
  mergeFlagCohorts,
} from './flag-cohort'

const recordingFlagCohorts = new Set()
const rejectedFlagCohortsByUser = new Map()
const storesRecordingOnFlagUpdate = new WeakSet()

const sameUser = (user, userId) => (user?.id === userId ? user : null)

export const knownFlagCohortsOf = async (fetchedUser, storedUser) => {
  const cachedUser = flags.canUseCacheProfileOnLoad
    ? sameUser(await getCachedCurrentUser().catch(() => null), fetchedUser.id)
    : null
  const flagCohorts = mergeFlagCohorts(
    mergeFlagCohorts(cachedUser?.flagCohorts, sameUser(storedUser, fetchedUser.id)?.flagCohorts),
    fetchedUser.flagCohorts,
  )
  return Object.keys(flagCohorts).length ? { flagCohorts } : {}
}

const rejectFlagCohortBatch = (error, flagBatch, allFlags, rejected) => {
  const isClientError = error?.status >= 400 && error?.status < 500
  const isCapReached = error?.status === 409
  if (isClientError) flagBatch.forEach((flagName) => rejected.add(flagName))
  if (isCapReached) allFlags.forEach((flagName) => rejected.add(flagName))
  return isCapReached
}

const updateFlagCohortsContext = (store, flagCohorts) => {
  return store.dispatch('featureToggles/updateFeatureContext', {
    flagCohorts: buildFlagCohortsContextValue(flagCohorts),
  })
}

const recordFlagCohortBatch = async ({
  store, userApi, flagBatch, userSwitched, requestingUserId,
}) => {
  if (userSwitched()) return
  const { flagCohorts: recorded } = await userApi.recordFlagCohorts(flagBatch)
  if (userSwitched()) return
  const flagCohorts = mergeFlagCohorts(store.state.currentUser.flagCohorts, recorded)
  store.commit('updateCurrentUser', { flagCohorts })
  if (flags.canUseCacheProfileOnLoad) {
    const cachedUser = await getCachedCurrentUser().catch(() => null)
    if (cachedUser?.id === requestingUserId) {
      await saveCachedCurrentUser({ ...cachedUser, flagCohorts })
    }
  }
  if (userSwitched()) return
  await updateFlagCohortsContext(store, flagCohorts)
}

export const recordFlagCohorts = async (store, userApi) => {
  const { currentUser } = store.state
  if (!currentUser?.id) return
  const requestingUserId = currentUser.id
  if (!rejectedFlagCohortsByUser.has(requestingUserId)) {
    rejectedFlagCohortsByUser.set(requestingUserId, new Set())
  }
  const rejected = rejectedFlagCohortsByUser.get(requestingUserId)
  const flagsToRecord = getFlagsToRecord({
    features: store.state.featureToggles.features,
    flagCohorts: currentUser.flagCohorts,
    inFlight: recordingFlagCohorts,
    rejected,
  })
  if (!flagsToRecord.length) return
  flagsToRecord.forEach((flagName) => recordingFlagCohorts.add(flagName))
  let stopRecording = false
  const userSwitched = () => {
    if (store.state.currentUser?.id !== requestingUserId) stopRecording = true
    return stopRecording
  }
  await chunkFlags(flagsToRecord).reduce(async (previousBatch, flagBatch) => {
    await previousBatch
    try {
      await recordFlagCohortBatch({
        store, userApi, flagBatch, userSwitched, requestingUserId,
      })
    } catch (error) {
      if (rejectFlagCohortBatch(error, flagBatch, flagsToRecord, rejected)) stopRecording = true
      sentry.captureException(error, { extra: { action: 'recordFlagCohorts', flagBatch } })
    } finally {
      flagBatch.forEach((flagName) => recordingFlagCohorts.delete(flagName))
    }
  }, Promise.resolve())
}

const recordFlagCohortsOnFlagUpdate = (store, userApi) => {
  const { unleashClient } = UnleashService
  if (!unleashClient || storesRecordingOnFlagUpdate.has(store)) return
  storesRecordingOnFlagUpdate.add(store)
  unleashClient.on('update', () => {
    if (store.getters['featureToggles/canUseFlagCohorts']) recordFlagCohorts(store, userApi)
  })
}

export const startFlagCohortRecording = async (store, userApi) => {
  recordFlagCohortsOnFlagUpdate(store, userApi)
  const authFlagsReady = await store.dispatch('featureToggles/whenAuthFlagsReady')
  if (authFlagsReady && store.getters['featureToggles/canUseFlagCohorts']) {
    const flagCohortsContext = buildFlagCohortsContextValue(store.state.currentUser?.flagCohorts)
    if (store.state.featureToggles.context?.flagCohorts !== flagCohortsContext) {
      await updateFlagCohortsContext(store, store.state.currentUser?.flagCohorts)
    }
    await recordFlagCohorts(store, userApi)
  }
}
